import React, { useEffect, useState } from "react";
import { AlertTriangle, ArrowRight, Flag, Loader2, Truck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";

/** SCH-1 §3 — the one readiness block every schedule and dispatch card shows. Theme tokens only. */
const DOT = { green: "bg-emerald-500", yellow: "bg-amber-400", amber: "bg-amber-400", red: "bg-red-500", grey: "bg-muted-foreground/40" };
const Dot = ({ colour, testId }) => <span data-testid={testId} data-colour={colour} className={`inline-block w-2 h-2 rounded-full shrink-0 ${DOT[colour] || DOT.grey}`} />;

export const ReadinessDots = ({ r, testId }) => (
  <span className="inline-flex items-center gap-1" data-testid={testId}>
    {["material", "submittals", "gc_ready", "co", "delivery", "drawings"].map((k) => (
      <span key={k} title={`${k.replace("_", " ")}: ${r?.[k]?.reason || "—"}`} className="inline-flex"><Dot colour={r?.[k]?.colour} testId={`${testId}-${k}`} /></span>
    ))}
  </span>
);

const Row = ({ label, item, testId }) => (
  <div className="flex items-start gap-2 text-[11px]" data-testid={testId} title={item?.reason || ""}>
    <span className="mt-1"><Dot colour={item?.colour} /></span>
    <span className="w-20 shrink-0 text-muted-foreground">{label}</span>
    <span className={`flex-1 min-w-0 truncate ${item?.colour === "green" ? "text-foreground" : "text-foreground/90"}`}>{item?.reason || "—"}</span>
  </div>
);

export default function CardReadiness({ mobId, projectId, onChanged, compact = false }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState("");
  const load = async () => { try { const { data } = await api.get(`/mobilizations/${mobId}/readiness`); setR(data); } catch { setR(null); } };
  useEffect(() => { if (mobId) load(); }, [mobId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!mobId) return null;
  if (!r) return <div className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="card-readiness-loading"><Loader2 className="w-3 h-3 animate-spin" /> readiness…</div>;

  const act = async (label, fn) => { setBusy(label); try { await fn(); await load(); window.dispatchEvent(new Event("schedule-alerts:invalidate")); onChanged?.(); } catch (e) { toast.error(e.response?.data?.detail?.detail || e.response?.data?.detail || `${label} failed`); } finally { setBusy(""); } };
  const gcReady = () => act("GC Ready", async () => {
    const note = window.prompt("GC Ready — confirmed with whom, and any note?", r.gc_ready?.note || "");
    if (note === null) throw new Error("cancelled");
    await api.post(`/mobilizations/${mobId}/gc-ready`, { confirmed: true, note, date: new Date().toISOString().slice(0, 10) });
  });
  const delivery = () => act("Delivery", async () => {
    const mode = window.prompt("Delivery slot — driver + vehicle, or 'direct ship'", r.delivery?.mode || "");
    if (mode === null) throw new Error("cancelled");
    await api.post(`/mobilizations/${mobId}/delivery`, { confirmed: true, mode });
  });
  const flag = () => act("Flag", async () => {
    const reason = window.prompt("Flag this card for the Scheduler — why?");
    if (!reason) throw new Error("cancelled");
    await api.post(`/mobilizations/${mobId}/flag`, { reason });
    toast.success("Flagged — the Scheduler has been notified");
  });
  const resolve = () => act("Resolve", async () => {
    const note = window.prompt(`Resolve the flag from ${r.review_flag?.by}: "${r.review_flag?.reason}" — note`);
    if (note === null) throw new Error("cancelled");
    await api.post(`/mobilizations/${mobId}/resolve-flag`, { reason: note });
  });
  const priority = () => act("Priority", async () => {
    if (r.priority?.on) { await api.post(`/projects/${projectId}/priority`, { on: false }); return; }
    const reason = window.prompt("Flag this job PRIORITY — reason");
    if (!reason) throw new Error("cancelled");
    await api.post(`/projects/${projectId}/priority`, { on: true, reason });
  });

  return (
    <div className="rounded border border-border bg-background p-2 space-y-1.5" data-testid={`card-readiness-${mobId}`}>
      <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        Readiness
        {r.gc?.rating && <span className="normal-case tracking-normal font-medium rounded border border-border px-1.5 py-px text-foreground" title={`GC rating from the directory — ${r.gc.company}`} data-testid="card-gc-rating">GC {r.gc.rating}</span>}
        {r.priority?.on && <span className="normal-case tracking-normal font-semibold rounded bg-foreground text-background px-1.5 py-px" title={`Priority — ${r.priority.reason} (${r.priority.by})`} data-testid="card-priority-chip">Priority{r.gc?.rating === "D" ? " · GC D" : ""}</span>}
        {r.review_flag && <span className="normal-case tracking-normal inline-flex items-center gap-1 text-amber-700 dark:text-amber-300" title={`${r.review_flag.by} (${r.review_flag.role}): ${r.review_flag.reason}`} data-testid="card-review-flag"><AlertTriangle className="w-3 h-3" /> flagged</span>}
        {(r.hours > 0) && <span className="normal-case tracking-normal font-normal ml-auto tabular-nums" data-testid="card-hours">{r.hours} h{r.crew ? ` · ${r.crew} crew` : ""}{r.days ? ` · ${r.days} d` : ""}</span>}
      </div>
      {compact ? <ReadinessDots r={r} testId={`readiness-dots-${mobId}`} /> : (
        <div className="space-y-0.5">
          <Row label="Material" item={r.material} testId="readiness-material" />
          <Row label="Submittals" item={r.submittals} testId="readiness-submittals" />
          <Row label="GC Ready" item={r.gc_ready} testId="readiness-gc-ready" />
          <Row label="CO" item={r.co} testId="readiness-co" />
          <Row label="Delivery" item={r.delivery} testId="readiness-delivery" />
          <Row label="Drawings" item={r.drawings} testId="readiness-drawings" />
        </div>
      )}
      <div className="flex items-center gap-1 flex-wrap pt-1">
        {r.can_gc_ready && !r.gc_ready?.confirmed && <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={gcReady} disabled={!!busy} data-testid="card-gc-ready-btn"><CheckCircle2 className="w-3 h-3 mr-1" /> GC Ready</Button>}
        {!r.delivery?.confirmed && <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={delivery} disabled={!!busy} data-testid="card-delivery-btn"><Truck className="w-3 h-3 mr-1" /> Delivery slot</Button>}
        {r.can_priority && <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={priority} disabled={!!busy} data-testid="card-priority-btn">{r.priority?.on ? "Clear priority" : "Priority"}</Button>}
        {!r.review_flag && <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={flag} disabled={!!busy} data-testid="card-flag-btn"><Flag className="w-3 h-3 mr-1" /> Flag</Button>}
        {r.review_flag && r.can_scheduler && <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={resolve} disabled={!!busy} data-testid="card-resolve-flag-btn">Resolve flag</Button>}
      </div>
    </div>
  );
}

/** Send to Dispatch with the §6 unsigned-CO prompt. `ids` = mob ids. Resolves true when sent. */
export async function sendToDispatchWithPrompt(ids, onDone) {
  const post = (confirm) => api.post("/mobilizations/bulk-status", { ids, status: "ready_to_dispatch", confirm_unsigned_co: confirm });
  try {
    let res;
    try { res = await post(false); } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.warning === "unsigned_co") {
        if (!window.confirm(d.detail)) return false;
        res = await post(true);
      } else throw e;
    }
    toast.success(`Sent ${ids.length} to Dispatch`);
    if (res.data?.pm_email_drafts?.length) toast.message("PM email drafted — review it in the job's email log before sending");
    onDone?.(res.data);
    return true;
  } catch (e) {
    const d = e.response?.data?.detail;
    toast.error(typeof d === "string" ? d : d?.detail || "Could not send");
    return false;
  }
}

export const SendArrow = ({ onClick, testId, title = "Send to Dispatch" }) => (
  <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} title={title} data-testid={testId}
          className="inline-flex items-center justify-center w-6 h-6 rounded border border-border text-foreground hover:bg-foreground hover:text-background transition-colors">
    <ArrowRight className="w-3.5 h-3.5" />
  </button>
);
